import { useState, useEffect } from 'react';
import { Cloud, Check, X, TestTube, Eye, EyeOff, Server, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api from '@/services/api';

const AssetStorageSettings = () => {
  const [settings, setSettings] = useState({
    provider: 'local',
    is_configured: false,
    bunny_storage_zone: '',
    bunny_api_key: '',
    bunny_pull_zone_url: '',
    bunny_region: '',
    cloudfront_distribution_id: '',
    cloudfront_domain: '',
    aws_access_key_id: '',
    aws_secret_access_key: '',
    aws_s3_bucket: '',
    aws_region: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showAwsSecret, setShowAwsSecret] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/assets/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({ title: 'Error', description: 'Failed to load settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/assets/settings', settings);
      setSettings(response.data.settings);
      toast({ title: 'Success', description: 'Asset storage settings saved successfully' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await api.post('/assets/test-connection');
      if (response.data.success) {
        toast({ title: 'Success', description: response.data.message });
      } else {
        toast({ title: 'Connection Failed', description: response.data.message, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Connection test failed', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const bunnyRegions = [
    { value: 'default', label: 'Falkenstein (Default)' },
    { value: 'ny', label: 'New York' },
    { value: 'la', label: 'Los Angeles' },
    { value: 'sg', label: 'Singapore' },
    { value: 'syd', label: 'Sydney' },
    { value: 'uk', label: 'London' },
    { value: 'se', label: 'Stockholm' },
    { value: 'br', label: 'São Paulo' },
    { value: 'jh', label: 'Johannesburg' }
  ];

  if (loading) {
    return (
      <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="text-center py-12 text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Cloud className="w-6 h-6 text-blue-600" />
              Asset Storage Settings
            </h1>
            <p className="text-gray-500 mt-1">Configure cloud storage for product images and other assets</p>
          </div>
          <Badge className={settings.is_configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
            {settings.is_configured ? (
              <><Check className="w-3 h-3 mr-1" /> Configured</>
            ) : (
              <><AlertTriangle className="w-3 h-3 mr-1" /> Not Configured</>
            )}
          </Badge>
        </div>

        {/* Provider Selection */}
        <Card className="bg-white border-gray-200 shadow-sm mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-gray-800">Storage Provider</CardTitle>
            <CardDescription>Select the cloud storage provider for your assets</CardDescription>
          </CardHeader>
          <CardContent>
            <Select 
              value={settings.provider} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, provider: v }))}
            >
              <SelectTrigger className="w-full max-w-md bg-gray-50 border-gray-300">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="bunny_cdn">
                  <div className="flex items-center gap-2">
                    <span className="text-orange-500">🐰</span>
                    Bunny CDN
                  </div>
                </SelectItem>
                <SelectItem value="cloudfront">
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-500">☁️</span>
                    AWS CloudFront + S3
                  </div>
                </SelectItem>
                <SelectItem value="local">
                  <div className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-gray-500" />
                    Local Storage (Not recommended)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Bunny CDN Settings */}
        {settings.provider === 'bunny_cdn' && (
          <Card className="bg-white border-gray-200 shadow-sm mb-6">
            <CardHeader className="pb-3 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <span className="text-orange-500">🐰</span>
                Bunny CDN Configuration
              </CardTitle>
              <CardDescription>
                Enter your Bunny CDN Storage Zone credentials. 
                <a href="https://dash.bunny.net/storage" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                  Get credentials from Bunny Dashboard
                </a>
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Storage Zone Name *</Label>
                  <Input 
                    value={settings.bunny_storage_zone || ''} 
                    onChange={(e) => setSettings(prev => ({ ...prev, bunny_storage_zone: e.target.value }))}
                    placeholder="my-storage-zone"
                    className="bg-gray-50 border-gray-300"
                  />
                  <p className="text-xs text-gray-500">The name of your Bunny Storage Zone</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Region</Label>
                  <Select 
                    value={settings.bunny_region || 'default'} 
                    onValueChange={(v) => setSettings(prev => ({ ...prev, bunny_region: v === 'default' ? '' : v }))}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-300">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {bunnyRegions.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">API Key (Password) *</Label>
                <div className="relative">
                  <Input 
                    type={showApiKey ? 'text' : 'password'}
                    value={settings.bunny_api_key || ''} 
                    onChange={(e) => setSettings(prev => ({ ...prev, bunny_api_key: e.target.value }))}
                    placeholder="Enter your Storage Zone API key"
                    className="bg-gray-50 border-gray-300 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">Found in Storage Zone → FTP & API Access → Password</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Pull Zone URL *</Label>
                <Input 
                  value={settings.bunny_pull_zone_url || ''} 
                  onChange={(e) => setSettings(prev => ({ ...prev, bunny_pull_zone_url: e.target.value }))}
                  placeholder="https://your-pullzone.b-cdn.net"
                  className="bg-gray-50 border-gray-300"
                />
                <p className="text-xs text-gray-500">Your Pull Zone hostname connected to this Storage Zone (for public URLs)</p>
              </div>

              {/* CORS Notice */}
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800 font-medium">⚠️ Important: Enable CORS in Bunny Dashboard</p>
                <p className="text-xs text-amber-700 mt-1">
                  For images to display correctly, enable CORS headers in your Pull Zone settings:
                  <br />Pull Zone → Headers → Add Header → <code className="bg-amber-100 px-1 rounded">Access-Control-Allow-Origin: *</code>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CloudFront Settings */}
        {settings.provider === 'cloudfront' && (
          <Card className="bg-white border-gray-200 shadow-sm mb-6">
            <CardHeader className="pb-3 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <span className="text-yellow-500">☁️</span>
                AWS CloudFront + S3 Configuration
              </CardTitle>
              <CardDescription>
                Configure AWS S3 bucket and CloudFront distribution for asset storage
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">S3 Bucket Name *</Label>
                  <Input 
                    value={settings.aws_s3_bucket || ''} 
                    onChange={(e) => setSettings(prev => ({ ...prev, aws_s3_bucket: e.target.value }))}
                    placeholder="my-assets-bucket"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">AWS Region *</Label>
                  <Input 
                    value={settings.aws_region || ''} 
                    onChange={(e) => setSettings(prev => ({ ...prev, aws_region: e.target.value }))}
                    placeholder="ap-south-1"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">AWS Access Key ID *</Label>
                  <Input 
                    value={settings.aws_access_key_id || ''} 
                    onChange={(e) => setSettings(prev => ({ ...prev, aws_access_key_id: e.target.value }))}
                    placeholder="AKIAIOSFODNN7EXAMPLE"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">AWS Secret Access Key *</Label>
                  <div className="relative">
                    <Input 
                      type={showAwsSecret ? 'text' : 'password'}
                      value={settings.aws_secret_access_key || ''} 
                      onChange={(e) => setSettings(prev => ({ ...prev, aws_secret_access_key: e.target.value }))}
                      placeholder="Enter your secret key"
                      className="bg-gray-50 border-gray-300 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setShowAwsSecret(!showAwsSecret)}
                    >
                      {showAwsSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">CloudFront Domain *</Label>
                <Input 
                  value={settings.cloudfront_domain || ''} 
                  onChange={(e) => setSettings(prev => ({ ...prev, cloudfront_domain: e.target.value }))}
                  placeholder="d1234567890.cloudfront.net"
                  className="bg-gray-50 border-gray-300"
                />
                <p className="text-xs text-gray-500">Your CloudFront distribution domain for serving assets</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Local Storage Warning */}
        {settings.provider === 'local' && (
          <Card className="bg-amber-50 border-amber-200 shadow-sm mb-6">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium">Local Storage Not Recommended</p>
                  <p className="text-amber-700 text-sm mt-1">
                    Local storage is not suitable for production. Images won&apos;t be served via CDN and 
                    may cause slow loading times. Please configure Bunny CDN or CloudFront for better performance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={testing || settings.provider === 'local' || !settings.is_configured}
            className="border-gray-300 text-gray-600"
          >
            {testing ? (
              <>Testing...</>
            ) : (
              <><TestTube className="w-4 h-4 mr-2" /> Test Connection</>
            )}
          </Button>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssetStorageSettings;
